import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, Shield } from "lucide-react";
import { IDS } from "@/lib/testIds";
import { useLang } from "@/lib/LangContext";

// Masked line-by-line reveal
const line = {
  hidden: { y: "110%" },
  show: (i = 0) => ({
    y: 0,
    transition: {
      duration: 1.2,
      ease: [0.76, 0, 0.24, 1],
      delay: 0.2 + i * 0.1,
    },
  }),
};

const HERO_IMG =
  "https://images.unsplash.com/photo-1517913451214-e22ce660e086?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxzZWN1cml0eSUyMGd1YXJkJTIwbmlnaHQlMjBwYXRyb2x8ZW58MHx8fHwxNzg0OTIzODM0fDA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const { ui } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -100]);

  return (
    <section
      ref={ref}
      data-testid={IDS.hero.root}
      className="relative h-[100svh] min-h-[720px] w-full overflow-hidden bg-background"
    >
      {/* Parallax image */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={HERO_IMG}
          alt="Security officer on night patrol in New Brunswick"
          className="w-full h-full object-cover grayscale contrast-125 brightness-[0.55]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-background/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/70 via-transparent to-background/40" />
      </motion.div>

      {/* Architectural grid overlay */}
      <div className="absolute inset-0 arch-grid opacity-[0.08] pointer-events-none" />

      {/* Corner meta */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute top-24 md:top-28 left-6 md:left-12 font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground z-10"
      >
        <div className="flex items-center gap-3">
          <span className="inline-block w-2 h-2 bg-primary animate-pulse" />
          {ui.hero.active}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="absolute top-24 md:top-28 right-6 md:right-12 font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground z-10 text-right"
      >
        <div>45.9636° N</div>
        <div>66.6431° W</div>
      </motion.div>

      {/* Main heading */}
      <motion.div
        style={{ y: textY, opacity }}
        className="relative z-10 h-full flex flex-col justify-end pb-20 md:pb-28 px-6 md:px-12 max-w-[1600px] mx-auto"
      >
        <div className="mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase text-muted-foreground mb-6 flex items-center gap-3"
          >
            <Shield size={12} strokeWidth={1.4} />
            {ui.hero.chip}
          </motion.div>

          <h1
            data-testid={IDS.hero.heading}
            className="font-serif font-light uppercase tracking-[-0.02em] leading-[0.92]"
            style={{ fontSize: "clamp(2rem, 7vw, 6rem)" }}
          >
            <span className="mask-line">
              <motion.span
                variants={line}
                initial="hidden"
                animate="show"
                custom={0}
                className="inline-block"
              >
                {ui.hero.line1}
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span
                variants={line}
                initial="hidden"
                animate="show"
                custom={1}
                className="inline-block italic font-normal"
              >
                {ui.hero.line2}
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span
                variants={line}
                initial="hidden"
                animate="show"
                custom={2}
                className="inline-block text-stroke-muted"
              >
                {ui.hero.line3}
              </motion.span>
            </span>
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8, ease: [0.76, 0, 0.24, 1] }}
            className="md:col-span-6 text-base md:text-lg font-light text-foreground/80 max-w-xl leading-relaxed"
          >
            {ui.hero.body}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.0, ease: [0.76, 0, 0.24, 1] }}
            className="md:col-span-6 flex md:justify-end items-center gap-4"
          >
            <Link
              to="/contact#quote"
              data-testid={IDS.hero.cta}
              className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-7 py-5 font-mono text-xs tracking-[0.2em] uppercase hover:bg-foreground hover:text-background transition-colors"
            >
              {ui.common.requestQuote}
              <span className="inline-block w-6 h-px bg-current group-hover:w-10 transition-all" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-services-link"
              className="hidden md:inline-flex items-center gap-2 border border-foreground px-7 py-5 font-mono text-xs tracking-[0.2em] uppercase hover:bg-foreground hover:text-background transition-colors"
            >
              {ui.common.viewServices}
            </Link>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.8 }}
        data-testid={IDS.hero.scroll}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-10"
      >
        <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-muted-foreground">
          {ui.common.scroll}
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={14} className="text-muted-foreground" />
        </motion.div>
      </motion.div>
    </section>
  );
}
